// src/MasterEntry/MainRoute/MainRoute.js
import React, { useContext, useEffect, useState, lazy, Suspense, useMemo } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../AuthContext/Authcontext";
import LoadingSpinner from "./LoadingSpinner ";
import CustomerTyreHistoryReport from "../Component/CustomerDashboard/components/CustomerTyreHistoryModal";

// Lazy load all components with prefetch
const Login = lazy(() => import("../LoginPages/Login"));
const Dashboard = lazy(() => import("../Component/Dashboard"));
const CustomerDashboard = lazy(() => import("../Component/CustomerDashboard"));
const AccurateLocation = lazy(() => import("../MasterPages/AccurateLocation"));
const CutLocation = lazy(() => import("../MasterPages/CutLocation"));
const Ntc = lazy(() => import("../MasterPages/Ntc"));
const TruckModel = lazy(() => import("../MasterPages/TruckModel"));
const TyrePossition = lazy(() => import("../MasterPages/TyrePossition"));
const TyreSize = lazy(() => import("../MasterPages/TyreSize"));
const UsedTyreType = lazy(() => import("../MasterPages/UsedTyreType"));
const TyreStatusType = lazy(() => import("../MasterPages/TyreStatusType"));
const TypeOfWear = lazy(() => import("../MasterPages/TypeOfWear"));
const TyreType = lazy(() => import("../MasterPages/TyreType"));
const TruckMake = lazy(() => import("../MasterPages/TruckMake"));
const ProtectedRoute = lazy(() => import("../../ProtectedRoute/ProtectedRoute"));
const Layout = lazy(() => import("../Layout/Layout"));

const Profile = lazy(() => import("../Setting/Profile"));
const ChangePassword = lazy(() => import("../Setting/ChangePassword"));
const ChangeEmail = lazy(() => import("../Setting/ChangeEmail"));
const SiteDetails = lazy(() => import("../ManageSite/SiteDetails"));
const UserEntry = lazy(() => import("../ManageUser/UserEntry"));
const SiteProject = lazy(() => import("../ManageSite/SiteProject"));
const SuperAdminProject = lazy(() => import("../ManageSite/SuperAdmin/SuperAdminProject"));
const SuperSiteEntry = lazy(() => import("../ManageSite/SuperAdmin/SuperSiteEntry"));
const SuperUserEntry = lazy(() => import("../ManageUser/SuperAdmin/SuperUserEntry"));
const SuperCustomerEntry = lazy(() => import("../ManageCustomer/SuperAdmin/SuperCustomerEntry"));
const DashboardLogo = lazy(() => import("../MasterPages/DashboardLogo/DashboardLogo"));
const TyreMake = lazy(() => import("../MasterPages/TyreMake"));
const TyreEntry = lazy(() => import("../Tyre/TyreEntry"));
const TyresDetails = lazy(() => import("../Tyre/TyresDetails"));
const TyreRemoval = lazy(() => import("../Tyre/TyreRemoval"));
const TyreFitmanEntry = lazy(() => import("../Tyre/TyreFitmanEntry"));
const TyreRepair = lazy(() => import("../Tyre/TyreRepair"));
const RoutineTask = lazy(() => import("../Tyre/RoutineTask"));
const ScrapTyreEntry = lazy(() => import("../Tyre/ScrapTyreEntry"));
const TyreExcelImport = lazy(() => import("../MasterPages/TyreExcelImport/TyreExcelImport"));
const RemovalResonCategory = lazy(() => import("../MasterPages/RemovalResonCategory/RemovalResonCategory"));
const TyreFitmentReport = lazy(() => import("../Component/CustomerDashboard/components/FitmentReport"));
const TyreHistoryReport = lazy(() => import("../Component/CustomerDashboard/components/TyreLifeReport"));
const TyreHistorySearch = lazy(() => import("../Report/TyreHistorySearch"));
const TyreRemovalReport = lazy(() => import("../Component/CustomerDashboard/components/RemovalReport"));
const TyreRepairReport = lazy(() => import("../Component/CustomerDashboard/components/RepairHistory"));
const RoutinTaskReport = lazy(() => import("../Report/RoutinTaskReport"));
const DownloadingIdS = lazy(() => import("../MasterPages/ButtonIDS/DownloadingIdS"));
const ResetForm = lazy(() => import("../LoginPages/ResertForm"));
const TyreStatusCategory = lazy(() => import("../MasterPages/TyreStatusCategory"));
const ItemCategory = lazy(() => import("../MasterPages/ItemCategory"));
const ItemStocks = lazy(() => import("../MasterPages/ItemStocks"));
const ItemUsed = lazy(() => import("../MasterPages/ItemUsed"));
const Purchase = lazy(() => import("../MasterPages/Purchase"));
const OtherEntry = lazy(() => import("../MasterPages/OtherEntry"));

const CPHTyreHistoryReport = lazy(() => import("../CPHReport/CPHTyreHistoryReport"));
const CPHStockReport = lazy(() => import("../CPHReport/CPHStockReport"));
const CPHPurchaseReport = lazy(() => import("../CPHReport/CPHPurchaseReport"));
const CPHUsedItemReport = lazy(() => import("../CPHReport/CPHUsedItemReport"));
const RaiseIssueModal = lazy(() => import("../../components/RaiseIssueModal"));

const CriticalTyreReport = lazy(() => import("../Component/CustomerDashboard/components/CriticalTyreReport"));
const MachineHistoryWrapper = lazy(() => import("../Component/CustomerDashboard/components/MachineHistory/MachineHistoryWrapper"));

const MainRoute = () => {
  const { isAuthenticated, isLoading, user } = useContext(AuthContext);
  const location = useLocation();
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    if (initialLoad) {
      setInitialLoad(false);
    }
  }, [initialLoad]);

  // Memoize navigation destination
  const dashboardPath = useMemo(() => {
    return user?.role_id === 4 ? "/customerdashboard" : "/dashboard";
  }, [user?.role_id]);

  if (isLoading || initialLoad) {
    return <LoadingSpinner />;
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Public Routes */}
        <Route path="reset-password" element={<ResetForm />} />
        <Route 
          path="/login" 
          element={
            isAuthenticated ? 
              <Navigate to={dashboardPath} replace /> : 
              <Login />
          } 
        />
        <Route
          path="/"
          element={
            isAuthenticated ? 
              <Navigate to={dashboardPath} replace /> : 
              <Navigate to="/login" replace />
          }
        />
        
        {/* Protected Routes wrapped inside Layout */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route 
            path="dashboard" 
            element={
              isAuthenticated && user?.role_id !== 4 ? 
                <Dashboard /> : 
                <Navigate to="/customerdashboard" replace />
            } 
          />
          
          <Route 
            path="customerdashboard" 
            element={
              isAuthenticated && user?.role_id === 4 ? 
                <CustomerDashboard mode="customer" /> : 
                <Navigate to="/dashboard" replace />
            } 
          />
          
          {/* Master Entry Routes - keep all existing routes */}
          <Route path="manage-accurate-location" element={<AccurateLocation />} />
          <Route path="manage-cut-location" element={<CutLocation />} />
          <Route path="ntc-tc-cut" element={<Ntc />} />
          <Route path="manage-tyre-model" element={<TruckModel />} />
          <Route path="manage-tyre-position" element={<TyrePossition />} />
          <Route path="manage-tyre-size" element={<TyreSize />} />
          <Route path="usedtyretype" element={<UsedTyreType />} />
          <Route path="tyre-status-type" element={<TyreStatusType />} />
          <Route path="typeofwear" element={<TypeOfWear />} />
          <Route path="manage-tyre-type" element={<TyreType />} />
          <Route path="manage-truck-make" element={<TruckMake />} />
          <Route path="downloadingids" element={<DownloadingIdS />} />

          {/* Settings Routes */}
          <Route path="profile" element={<Profile />} />
          <Route path="changepassword" element={<ChangePassword />} />
          <Route path="changeemail" element={<ChangeEmail />} />
          <Route path="sitedetails" element={<SiteDetails />} />
          <Route path="userentry" element={<UserEntry />} />
          <Route path="siteproject" element={<SiteProject />} />
          <Route path="superadminsiteproject" element={<SuperAdminProject />} />
          <Route path="supersiteentry" element={<SuperSiteEntry />} />
          <Route path="superuserentry" element={<SuperUserEntry />} />
          <Route path="supercustomerentry" element={<SuperCustomerEntry />} />
          <Route path="dashboardlogo" element={<DashboardLogo />} />
          
          {/* Tyre Management Routes */}
          <Route path="tyremake" element={<TyreMake />} />
          <Route path="tyreentry" element={<TyreEntry />} />          
          <Route path="/routinetask" element={<RoutineTask />} />
          <Route path="tyresdetails" element={<TyresDetails />} />
          <Route path="tyreremoval" element={<TyreRemoval />} />          
          <Route path="/tyrerepairentry" element={<TyreRepair />} />
          <Route path="tyrefitmanentry" element={<TyreFitmanEntry />} />
          <Route path="scrapTyreEntry" element={<ScrapTyreEntry />} />
          
          {/* Excel Import & Other Routes */}
          <Route path="/superexcelimport" element={<TyreExcelImport />} />
          <Route path="/removalresoncategory" element={<RemovalResonCategory />} />
          
          {/* Reporting Routes */}
          <Route path="/tyrefitmentreport" element={<TyreFitmentReport />} />
          <Route path="tyrehistoryreport" element={<TyreHistoryReport />} />
          <Route path="customertyrehistoryreport" element={<CustomerTyreHistoryReport />} />
          <Route path="tyrehistorysearch" element={<TyreHistorySearch />} />
          <Route path="tyreremovalreport" element={<TyreRemovalReport />} />
          <Route path="tyrerepairreport" element={<TyreRepairReport />} />
          <Route path="tyreroutintaskreport" element={<RoutinTaskReport />} />
          <Route path="tyrestatuschange" element={<TyreStatusCategory />} />
          
          <Route path="criticaltyrereport" element={<CriticalTyreReport />} />
          <Route path="machine-history" element={<MachineHistoryWrapper sharedData={{ statusOptions: [] }} />} />
          
          {/* CPH Inventory Routes */}
          <Route path="itemcategories" element={<ItemCategory />} />
          <Route path="itemstocks" element={<ItemStocks />} />
          <Route path="itemused" element={<ItemUsed />} />
          <Route path="purchase" element={<Purchase />} />
          <Route path="otherentry" element={<OtherEntry />} />
          
          {/* CPH Report Routes */}
          <Route path="cphtyrehistoryreport" element={<CPHTyreHistoryReport />} />
          <Route path="cphstockreport" element={<CPHStockReport />} />
          <Route path="cphpurchasereport" element={<CPHPurchaseReport />} />
          <Route path="cphuseditemreport" element={<CPHUsedItemReport />} />
          
          <Route path="RaiseIssueModal" element={<RaiseIssueModal />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default MainRoute;